// Setup › DATA › Costs (`/c/{slug}/setup/costs`; coverage audit §5 row 11, rows 57-59): the
// company cost ledger of one month with its roll-up (`GET /costs?period=`), a manual cost row
// (`POST /costs`, managers) and the reversal of a row (`POST /costs/{id}/reverse`, behind an
// AlertDialog). The ledger is append-only: a correction is a reversal, never an edit. Agency
// rows (and the visibility column) appear for agency operators only; the server filters them.
// Two calls on load: the month's ledger and the seats (names and the seat picker).
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Toolbar,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, ReceiptIcon, Undo2Icon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CostEvent } from "../../api/types";
import { formatMicros } from "../agents/money";
import type { CompanyAccess } from "../company";
import { COST_KIND_LABELS, formatDate, formatMoney, fractionDigits } from "../format";
import { problemText } from "../ops-parts";
import { COST_PAGE, opsKeys } from "../ops-queries";
import { seatsQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";

/** Kinds a manager may post by hand (`reversal` and `human_time` are the platform's). */
const MANUAL_KINDS = [
  "adjustment",
  "de_subscription",
  "de_usage",
  "agent_tokens",
  "agent_compute",
  "agent_tool",
] as const;
type ManualKind = (typeof MANUAL_KINDS)[number];

/** `YYYY-MM` of a date (local calendar). */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function recentMonths(today: Date, count = 12): string[] {
  return Array.from({ length: count }, (_, i) =>
    monthKey(new Date(today.getFullYear(), today.getMonth() - i, 1)),
  );
}

function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return new Date(y ?? 2000, (m ?? 1) - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

/** Whether `row` is a reversal or a compensation of another row (not reversible itself). */
const isCorrection = (row: CostEvent) =>
  row.kind === "reversal" || row.meter_identity.startsWith("reversal:");

/** Why the row cannot be reversed (the server's `unreversible`), or null. */
function notReversible(row: CostEvent, reversed: ReadonlySet<string>): string | null {
  if (isCorrection(row)) return "A correction; post a new row instead";
  if (row.kind === "human_time") return "Follows its time entry; correct the entry";
  if (row.amount_minor === 0) return "Nothing to reverse";
  if (reversed.has(row.id)) return "Reversed";
  return null;
}

export function CostsPage({ api, company }: { api: Api; company: CompanyAccess }) {
  const months = useMemo(() => recentMonths(new Date()), []);
  const [month, setMonth] = useState(months[0] ?? monthKey(new Date()));
  const operator = company.roles.includes("agency_operator");
  const costs = useInfiniteQuery({
    queryKey: opsKeys.costs(company.id, month, "all"),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.costs.list({
        params: { co: company.id },
        query: { period: month, limit: COST_PAGE, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.ledger.next_cursor ?? undefined,
  });
  const seats = useInfiniteQuery(seatsQuery(api, company.id));
  const seatList = seats.data?.pages.flatMap((p) => p.data) ?? [];
  const seatName = (id: string | null) =>
    id ? (seatList.find((s) => s.id === id)?.title ?? `…${id.slice(-6)}`) : "Company";
  const rows = costs.data?.pages.flatMap((p) => p.ledger.data) ?? [];
  const rollup = costs.data?.pages[0]?.rollup;
  const currency = rollup?.currency ?? "USD";
  // A reversal names its original; a credit's compensation names it in `source_ref`.
  const reversed = new Set(
    rows.flatMap((r) =>
      r.reverses_cost_event_id
        ? [r.reverses_cost_event_id]
        : r.source_ref?.system === "cost_event" && r.source_ref.kind === "reversal"
          ? [r.source_ref.id]
          : [],
    ),
  );
  const kinds = Object.entries(rollup?.by_kind ?? {})
    .filter(([, v]) => v !== 0)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));

  return (
    <SetupPage
      title="Costs"
      {...(costs.data ? { count: rows.length } : {})}
      actions={
        rollup ? (
          <AddCostDialog
            api={api}
            company={company}
            currency={currency}
            seats={seatList}
            operator={operator}
          />
        ) : null
      }
    >
      <Toolbar>
        <NativeSelect
          aria-label="Month"
          size="sm"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {months.map((m) => (
            <NativeSelectOption key={m} value={m}>
              {monthLabel(m)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Toolbar>
      {costs.isPending ? (
        <Loading label="Loading costs" rows={4} />
      ) : costs.isError ? (
        <LoadError what="Costs" error={costs.error} />
      ) : (
        <>
          <StatGrid
            data-slot="cost-rollup"
            aria-label={`${monthLabel(month)} totals`}
            className="my-0 gap-3"
          >
            <StatTile
              label="Total"
              value={formatMoney(rollup?.total_minor ?? 0, currency)}
              detail={monthLabel(month)}
              className={TILE}
            />
            {kinds.slice(0, 3).map(([kind, minor]) => (
              <StatTile
                key={kind}
                label={COST_KIND_LABELS[kind] ?? kind}
                value={formatMoney(minor, currency)}
                className={TILE}
              />
            ))}
          </StatGrid>
          {rows.length === 0 ? (
            <EmptyState
              variant="card"
              icon={ReceiptIcon}
              title={`No costs in ${monthLabel(month)}`}
              description="Add a manual row for anything the platform does not post."
            />
          ) : (
            <div className="flex min-w-0 flex-col gap-3">
              <Table data-slot="cost-ledger" className={DENSE_TABLE}>
                <TableCaption className="sr-only">
                  {monthLabel(month)}, newest first.{" "}
                  {operator ? "Agency rows are visible to agency operators only." : ""}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Date</TableHead>
                    <TableHead scope="col">Kind</TableHead>
                    <TableHead scope="col" className="max-md:hidden">
                      Seat
                    </TableHead>
                    <TableHead scope="col" className="max-md:hidden">
                      Reference
                    </TableHead>
                    {operator ? (
                      <TableHead scope="col" className="max-lg:hidden">
                        Visibility
                      </TableHead>
                    ) : null}
                    <TableHead scope="col" className="text-right">
                      Amount
                    </TableHead>
                    <TableHead scope="col">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const why = notReversible(row, reversed);
                    return (
                      <TableRow key={row.id} data-cost={row.id} className="group/row">
                        <TableCell className="text-muted-foreground tabular-nums">
                          {formatDate(row.occurred_at)}
                        </TableCell>
                        <TableHead scope="row" className="font-normal">
                          {COST_KIND_LABELS[row.kind] ?? row.kind}
                        </TableHead>
                        <TableCell className="max-md:hidden">{seatName(row.seat_id)}</TableCell>
                        <TableCell className="max-w-72 truncate text-muted-foreground max-md:hidden">
                          <span title={row.meter_identity}>
                            {row.source_ref?.note ?? row.meter_identity}
                          </span>
                          {row.source_ref?.system === "agent_reported" && !reversed.has(row.id) ? (
                            <Badge variant="warning" className="ml-2" data-agent-reported>
                              Reported by agent
                            </Badge>
                          ) : null}
                        </TableCell>
                        {operator ? (
                          <TableCell className="max-lg:hidden">
                            <Badge variant={row.visibility === "agency" ? "accent" : "neutral"}>
                              {row.visibility === "agency" ? "Agency" : "Client"}
                            </Badge>
                          </TableCell>
                        ) : null}
                        <TableCell className="text-right tabular-nums">
                          {/* AGENT-PLUMBING: metered rows keep their sub-cent amount */}
                          {row.amount_micros != null
                            ? formatMicros(row.amount_micros, row.currency)
                            : formatMoney(row.amount_minor, row.currency)}
                        </TableCell>
                        <TableCell className="w-px text-right">
                          {why ? (
                            why === "Reversed" ? (
                              <Badge variant="neutral">Reversed</Badge>
                            ) : (
                              <span className="sr-only">{why}</span>
                            )
                          ) : (
                            <RowActions>
                              <ReverseDialog api={api} company={company} row={row} />
                            </RowActions>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <LoadMore
                what="costs"
                hasMore={costs.hasNextPage}
                loading={costs.isFetchingNextPage}
                onMore={() => void costs.fetchNextPage()}
              />
            </div>
          )}
        </>
      )}
    </SetupPage>
  );
}

/** A compact stat tile (Setup is dense). */
const TILE = "px-4 py-3 [&>span:nth-child(2)]:mt-1 [&>span:nth-child(2)]:text-xl";

function ReverseDialog({
  api,
  company,
  row,
}: {
  api: Api;
  company: CompanyAccess;
  row: CostEvent;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const id = useId();
  const reverse = useMutation({
    mutationFn: () =>
      api.client.costs.reverse({
        params: { co: company.id, id: row.id },
        idempotencyKey: newIdempotencyKey(),
        body: { reason: reason.trim() },
      }),
    onSuccess: async () => {
      setOpen(false);
      setReason("");
      await qc.invalidateQueries({ queryKey: opsKeys.costsAll(company.id) });
    },
  });
  const amount = formatMoney(row.amount_minor, row.currency);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <RowAction
        icon={Undo2Icon}
        label={`Reverse ${COST_KIND_LABELS[row.kind] ?? row.kind} ${amount}`}
        tip="Reverse"
        onClick={() => setOpen(true)}
      />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reverse this {amount} row?</AlertDialogTitle>
          <AlertDialogDescription>
            Posts a row of {formatMoney(-row.amount_minor, row.currency)} on the same date, so the
            month nets to zero. The original stays in the ledger; a reversal cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Field>
          <FieldLabel htmlFor={id}>Reason</FieldLabel>
          <Textarea
            id={id}
            rows={2}
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {reverse.isError ? (
            <FieldError>{problemText(reverse.error, "The row could not be reversed.")}</FieldError>
          ) : null}
        </Field>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={!reason.trim() || reverse.isPending}
            onClick={() => reverse.mutate()}
          >
            {reverse.isPending ? "Reversing…" : "Reverse row"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** "12.50" in the currency's minor digits → 1250; null when not a number. */
export function toMinor(value: string, currency: string): number | null {
  const v = value.trim().replace(/,/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(v)) return null;
  const digits = fractionDigits(currency);
  const [whole = "0", frac = ""] = v.replace("-", "").split(".");
  if (frac.length > digits) return null;
  const minor = Number(whole) * 10 ** digits + Number(frac.padEnd(digits, "0") || "0");
  return v.startsWith("-") ? -minor : minor;
}

function AddCostDialog({
  api,
  company,
  currency,
  seats,
  operator,
}: {
  api: Api;
  company: CompanyAccess;
  currency: string;
  seats: { id: string; title: string }[];
  operator: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ManualKind>("adjustment");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [seat, setSeat] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [visibility, setVisibility] = useState<"client" | "agency">("client");
  const ids = {
    kind: useId(),
    amount: useId(),
    date: useId(),
    seat: useId(),
    ref: useId(),
    note: useId(),
    vis: useId(),
  };
  const minor = toMinor(amount, currency);
  const negativeNotAllowed = minor !== null && minor < 0 && kind !== "adjustment";
  const create = useMutation({
    mutationFn: () => {
      // The meter identity dedupes a posting: the reference (an invoice number) when given,
      // else a fresh id; `manual:` is not one of the platform's reserved systems.
      const ref = reference.trim().replace(/\s+/g, "-") || newIdempotencyKey();
      return api.client.costs.create({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          kind,
          amount_minor: minor ?? 0,
          currency: currency as never,
          // Midday of the chosen day, but never ahead of now: the API refuses a cost more than
          // 5 minutes in the future, and today's midday UTC is ahead until 12:00 UTC.
          occurred_at: new Date(
            Math.min(Date.parse(`${date}T12:00:00.000Z`), Date.now()),
          ).toISOString(),
          meter_identity: `manual:${kind}:${ref}`,
          ...(seat ? { seat_id: seat } : {}),
          ...(operator ? { visibility } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      });
    },
    onSuccess: async () => {
      setOpen(false);
      setAmount("");
      setReference("");
      setNote("");
      await qc.invalidateQueries({ queryKey: opsKeys.costsAll(company.id) });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (minor !== null && !negativeNotAllowed) create.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" onClick={() => setOpen(true)}>
        <PlusIcon data-icon="inline-start" aria-hidden="true" />
        Add cost
      </Button>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Add a cost</DialogTitle>
            <DialogDescription>
              A manual row in {currency}, the company's ledger currency. Only an adjustment may be
              negative (a credit).
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor={ids.kind}>Kind</FieldLabel>
                <NativeSelect
                  id={ids.kind}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as ManualKind)}
                >
                  {MANUAL_KINDS.map((k) => (
                    <NativeSelectOption key={k} value={k}>
                      {COST_KIND_LABELS[k] ?? k}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field data-invalid={negativeNotAllowed || (amount !== "" && minor === null)}>
                <FieldLabel htmlFor={ids.amount}>Amount ({currency})</FieldLabel>
                <Input
                  id={ids.amount}
                  inputMode="decimal"
                  required
                  value={amount}
                  aria-invalid={negativeNotAllowed || (amount !== "" && minor === null)}
                  onChange={(e) => setAmount(e.target.value)}
                />
                {amount !== "" && minor === null ? (
                  <FieldError>Enter an amount like 12.50.</FieldError>
                ) : negativeNotAllowed ? (
                  <FieldError>Only an adjustment may be negative.</FieldError>
                ) : null}
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.date}>Date</FieldLabel>
                <Input
                  id={ids.date}
                  type="date"
                  required
                  max={new Date().toISOString().slice(0, 10)}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.seat}>Seat</FieldLabel>
                <NativeSelect id={ids.seat} value={seat} onChange={(e) => setSeat(e.target.value)}>
                  <NativeSelectOption value="">Company (no seat)</NativeSelectOption>
                  {seats.map((s) => (
                    <NativeSelectOption key={s.id} value={s.id}>
                      {s.title}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor={ids.ref}>Reference</FieldLabel>
              <Input
                id={ids.ref}
                value={reference}
                maxLength={120}
                placeholder="Invoice number (optional)"
                onChange={(e) => setReference(e.target.value)}
              />
              <FieldDescription>
                The same reference twice posts once: it is the row's meter identity.
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.note}>Note</FieldLabel>
              <Input
                id={ids.note}
                value={note}
                maxLength={2000}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
            {operator ? (
              <Field>
                <FieldLabel htmlFor={ids.vis}>Visible to</FieldLabel>
                <NativeSelect
                  id={ids.vis}
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as "client" | "agency")}
                >
                  <NativeSelectOption value="client">Client and agency</NativeSelectOption>
                  <NativeSelectOption value="agency">Agency only</NativeSelectOption>
                </NativeSelect>
              </Field>
            ) : null}
            {create.isError ? (
              <FieldError>{problemText(create.error, "The cost could not be added.")}</FieldError>
            ) : null}
          </FieldGroup>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button
              type="submit"
              disabled={create.isPending || minor === null || negativeNotAllowed}
            >
              {create.isPending ? "Adding…" : "Add cost"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
